import React, { ReactNode } from 'react';
import { Box, Sheet, Stack, Typography, Alert } from '@mui/joy';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <Sheet
      variant="plain"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.body',
        py: 2,
        mb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      <Box>
        <Typography level="h3" sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && <Typography level="body-sm" color="neutral">{subtitle}</Typography>}
      </Box>
      <Stack direction="row" spacing={1}>{actions}</Stack>
    </Sheet>
  );
}

interface SectionCardProps {
  title?: string;
  description?: string;
  children: ReactNode;
}

export function SectionCard({ title, description, children }: SectionCardProps) {
  return (
    <Sheet
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 'lg',
        boxShadow: 'sm',
        bgcolor: 'background.surface'
      }}
    >
      {(title || description) && (
        <Box sx={{ mb: 2 }}>
          {title && <Typography level="title-md" sx={{ fontWeight: 600 }}>{title}</Typography>}
          {description && <Typography level="body-sm" color="neutral">{description}</Typography>}
        </Box>
      )}
      {children}
    </Sheet>
  );
}

interface PageMessageProps {
  message: { type: 'success' | 'danger'; text: string } | null;
}

export function PageMessage({ message }: PageMessageProps) {
  if (!message) return null;
  return <Alert color={message.type} variant="soft" sx={{ mb: 2 }}>{message.text}</Alert>;
}
